/**
 * Checks if a personnel record matches the search query.
 * For name queries, matches the continuous text entered, or every word in any order.
 * Also preserves matching for emails, divisions, phone numbers, payers, and types.
 */
export function matchesPersonnelSearch(staff, search) {
  if (!staff || typeof staff !== "object") return false;
  if (!search) return true;
  const s = String(search).trim().toLowerCase().replace(/\s+/g, " ");
  if (!s) return true;

  // 1. Full name match (first last, display name, or last first)
  const fullName = `${staff.first_name || ""} ${staff.last_name || ""}`.trim().toLowerCase().replace(/\s+/g, " ");
  const nameField = String(staff.name || "").trim().toLowerCase().replace(/\s+/g, " ");
  const reverseName = `${staff.last_name || ""} ${staff.first_name || ""}`.trim().toLowerCase().replace(/\s+/g, " ");

  if (fullName.includes(s) || nameField.includes(s) || reverseName.includes(s)) {
    return true;
  }

  // Multi-word: every word somewhere in the name, any order
  // ("pia zuniga" matches "Pia Francisca Zuniga Orellana").
  const words = s.split(" ");
  if (
    words.length > 1 &&
    [fullName, nameField].some((n) => n && words.every((w) => n.includes(w)))
  ) {
    return true;
  }

  // 2. Email match
  const email = String(staff.email || "").trim().toLowerCase();
  if (email.includes(s)) return true;

  // 3. Division match
  const division = String(staff.division || staff.division_name || staff.divisionName || "").trim().toLowerCase();
  if (division.includes(s)) return true;

  // 4. Phone match (substring or continuous digits)
  const phone = String(staff.phone || staff.phone_mobile || staff.phone_home || "").trim();
  const phoneDigits = phone.replace(/\D/g, "");
  const searchDigits = s.replace(/\D/g, "");
  if (phone.toLowerCase().includes(s)) return true;
  if (!s.includes("@") && searchDigits.length >= 3 && phoneDigits.includes(searchDigits)) return true;

  // 5. Payer match
  const payer = String(staff.payer || staff.payer_name || staff.agency_name || "").trim().toLowerCase();
  if (payer.includes(s)) return true;

  // 6. Type match (handles "Field Staff", "fieldstaff", "Outsourced Staff", "outsourcedstaff", "Candidate", etc.)
  const rawType = String(staff.type || staff.account_type || "").trim().toLowerCase();
  const normType = rawType.replace(/[\s_-]/g, "");
  const normSearch = s.replace(/[\s_-]/g, "");

  const typeLabels = [rawType, normType];
  if (normType === "fieldstaff") {
    typeLabels.push("field staff", "field");
  } else if (normType === "outsourcedstaff") {
    typeLabels.push("outsourced staff", "outsourced");
  } else if (normType === "candidate") {
    typeLabels.push("candidate");
  } else if (normType === "office" || normType === "officestaff") {
    typeLabels.push("office staff", "office", "officestaff");
  }

  if (s === "staff" || normSearch === "staff") {
    if (normType.includes("staff")) return true;
  } else if (
    typeLabels.some(
      (label) =>
        label === s ||
        label === normSearch ||
        label.startsWith(s) ||
        (s.length >= 4 && label.includes(s)),
    )
  ) {
    return true;
  }

  // 7. Account / Employee ID match
  const idStr = String(staff.id ?? staff.account_id ?? staff.accountId ?? "");
  if (idStr.includes(s)) return true;

  // 8. Role match (Office Staff ACL roles / roles list)
  if (staff.roles) {
    if (typeof staff.roles === "string" && staff.roles.toLowerCase().includes(s)) {
      return true;
    }
    if (Array.isArray(staff.roles)) {
      const matchInRoles = staff.roles.some((r) => {
        const roleName = typeof r === "string" ? r : r?.name || r?.label || "";
        return String(roleName).toLowerCase().includes(s);
      });
      if (matchInRoles) return true;
    }
  }

  if (Array.isArray(staff.aclRoles)) {
    const matchInAcl = staff.aclRoles.some((r) => {
      const roleName = String(r?.name || "").toLowerCase();
      const objName = String(r?.objectName || "").toLowerCase();
      return roleName.includes(s) || `${roleName}: ${objName}`.includes(s);
    });
    if (matchInAcl) return true;
  }

  if (staff.role && String(staff.role).toLowerCase().includes(s)) {
    return true;
  }

  return false;
}

/**
 * Sorts personnel records in alphabetical order by name (case-insensitive).
 */
export function sortPersonnelAlphabetically(a, b) {
  const nameA = String(a?.name || `${a?.first_name || ""} ${a?.last_name || ""}`).trim();
  const nameB = String(b?.name || `${b?.first_name || ""} ${b?.last_name || ""}`).trim();
  return nameA.localeCompare(nameB, undefined, { sensitivity: "base" });
}
